import defaultUser from '../utils/default-user';
import * as Fnctn from '../contexts/GenericFunctions'

type SignInErrorType = 'invalid_credentials' | 'network' | 'unknown';

async function extractErrorMessage(response: Response, fallbackMessage: string) {
  let message = fallbackMessage;

  try {
    const data = await response.clone().json();
    const backendMessage = data?.message ?? data?.error ?? data?.detail;
    if (typeof backendMessage === 'string' && backendMessage.trim().length > 0) {
      message = backendMessage;
      return message;
    }
  } catch {
    // Ignore JSON parsing errors and fallback to reading raw text below.
  }

  try {
    const text = await response.clone().text();
    if (text.trim().length > 0) {
      message = text;
    }
  } catch {
    // Ignore text parsing errors and keep the fallback message.
  }

  return message;
}

export async function signIn(email: string, password: string) {
  try {
    const api: string = Fnctn.GetAPI();
    let params: Record<string, string> = {
      'user': email,
      'password': password
    };

    let response = await fetch(`${api}/token/generate`, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });

    if (!response.ok) {
      const message = await extractErrorMessage(response, 'No se pudo generar el token de autenticación.');
      const error = new Error(message) as Error & { errorType: SignInErrorType };
      error.errorType = 'network';
      throw error;
    }

    const token = await response.json();
    const tokenValue = token?.data?.token;
    if (!tokenValue) {
      throw new Error('La respuesta del servidor no incluye un token de autenticación.');
    }

    params = {
      'user': email,
      'password': password
    };

    response = await fetch(`${api}/login/access`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenValue}`
      },
      body: JSON.stringify(params),

    });

    if (!response.ok) {
      const message = await extractErrorMessage(response, 'No se pudo completar el inicio de sesión.');
      const error = new Error(message) as Error & { errorType: SignInErrorType };
      error.errorType = 'network';
      throw error;
    }

    const loginResult = await response.json();

    if (!Array.isArray(loginResult?.data) || !loginResult.data[0]) {
      throw new Error('La respuesta del servidor no contiene información del usuario.');
    }

    const loginData = loginResult.data[0];
    const { password: _omittedPassword, ...loginDataWithoutPassword } = loginData;
    const sanitizedLoginData = { ...loginDataWithoutPassword, token: tokenValue };

    Fnctn.storeAuthData(sanitizedLoginData);
    if (typeof document !== 'undefined') {
      document.cookie = "dtUsuario=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;";
    }

    defaultUser.email = loginData.name_employee;
    defaultUser.avatarUrl = "../../public/logo_territoriohalcon.png";
    defaultUser.valido = loginResult.valid;

    localStorage.setItem("user_id", loginData._id);

    if (!loginResult.valid) {
      return {
        isOk: false,
        message: loginResult?.message ?? 'El usuario y/o contraseña es incorrecto.',
        errorType: 'invalid_credentials' as SignInErrorType
      };
    }

    return {
      isOk: true,
      data: { ...defaultUser }
    };
  }
  catch (error) {
    if (error instanceof Error && 'errorType' in error) {
      throw error;
    }

    const unknownError = error instanceof Error ? error : new Error('Error desconocido al iniciar sesión.');
    (unknownError as Error & { errorType: SignInErrorType }).errorType = 'unknown';
    throw unknownError;
  }
}




export async function resetPassword(email: string) {
  try {
    // Send request
    console.log(email);
    return {
      isOk: true
    };
  }
  catch {
    return {
      isOk: false,
      message: "Failed to reset password"
    };
  }
}
export async function refreshSession(userId: string) {
  try {
    const storedCredentials = Fnctn.getCookie();
    if (!storedCredentials?.token) {
      return {
        isOk: false,
        message: 'No hay un token de autenticación disponible.',
      };
    }

    const api = Fnctn.GetAPI();
    const response = await fetch(`${api}/login/ReloadAccess`, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${storedCredentials.token}`
      },
      body: JSON.stringify({ user_id: userId }),
    });

    if (!response.ok) {
      const message = await extractErrorMessage(response, 'No se pudo validar la sesión.');
      return {
        isOk: false,
        message,
      };
    }

    const payload = await response.json();
    let sessionData = payload?.data?.[0];
    sessionData.cvetra = storedCredentials.cvetra;
    sessionData.data_area = storedCredentials.data_area;
    sessionData.data_modules = storedCredentials.data_modules;
    sessionData.data_role = storedCredentials.data_role;
    sessionData.job_code = storedCredentials.job_code;
    sessionData.name_employee = storedCredentials.name_employee;
    if (!sessionData) {
      return {
        isOk: false,
        message: 'La respuesta del servidor no contiene información del usuario.',
      };
    }

    const { password: password, token: sessionToken, ...restSessionData } = sessionData;
    const tokenToPersist = sessionToken ?? storedCredentials.token;
    const sanitizedSessionData = { ...restSessionData, token: tokenToPersist };

    if (payload?.valid === false) {
      defaultUser.valido = false;
      Fnctn.clearAuthData();
      try {
        localStorage.removeItem('user_id');
      } catch {
        // Ignore localStorage errors in non-browser environments.
      }
      return {
        isOk: false,
        message: payload?.message ?? 'La sesión no es válida.',
      };
    }

    Fnctn.storeAuthData(sanitizedSessionData);

    if (sessionData.name_employee) {
      defaultUser.email = sessionData.name_employee;
    }
    defaultUser.valido = true;

    try {
      localStorage.setItem('user_id', sessionData._id ?? userId);
    } catch {
      // Ignore localStorage errors in non-browser environments.
    }

    return {
      isOk: true,
      data: { ...defaultUser },
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'No fue posible validar la sesión.';
    return {
      isOk: false,
      message,
    };
  }
}